import { first } from "rxjs/operators";
import { firestore } from "@/firebase";
import { splitPath, pathHead, pathTail, cleanupObj } from "@/store/helpers";
import { CACHED, CONTEXT_DOC, MODULE_NAME } from "../helpers/getter.type";
import {
  CACHE,
  GET,
  GET_FROM_MODULES,
  G_TEMPLATES_CACHE,
  WATCH_DOCUMENT,
  WATCH_ITEMS,
} from "../helpers/action.type";

export { cache, fetch_document, watch_document } from "../helpers/actions.store";

export async function get({ dispatch, getters }, payload) {
  await dispatch(CACHE, payload?.options);
  await dispatch(G_TEMPLATES_CACHE, {
    ...payload,
    path: getters[MODULE_NAME],
  }, { root: true });

  if (pathHead(payload?.path))
    return await dispatch(GET_FROM_MODULES, payload);
  else
    return getters[CACHED];
}

export async function update({ getters }, context) {
  const doc_ref = getters.ref;
  const col_ref = doc_ref.collection("items");
  const batch = firestore.batch();
  
  const updateObj = {
    rating_date: context.rating_date,
    goal_deadline: context.goal_deadline
  };
  batch.update(doc_ref, cleanupObj(updateObj));
  
  forEachItem(context, (cat, item) => {
    const item_data = {
      grade: item.grade,
      goal: item.goal,
      path: getters.path(cat.id, item.id)
    };
    const ref = item.doc_id ? col_ref.doc(item.doc_id) : col_ref.doc();
    batch.set(ref, cleanupObj(item_data));
  });
  return await batch.commit();
}

export async function fetch({ dispatch, commit }) {
  try {
    await Promise.all([ dispatch("fetch_document"), dispatch("fetch_items") ]);
    commit("set_fetch_timestamp");
  } catch (error) {
    console.error(error);
    throw Error("An error occurred while fetching the context doc and/or the items of the context.");
  }
}

export async function watch({ dispatch, commit }) {
  try {
    await Promise.all([ dispatch(WATCH_DOCUMENT), dispatch(WATCH_ITEMS) ]);
    commit("set_fetch_timestamp");
  } catch (error) {
    console.error(error);
    throw Error("An error occurred while watching the context doc and/or the items of the context.");
  }
}

export async function fetch_items({ dispatch, getters }) {
  try {
    const items_snapshot = await getters.ref.collection("items").get();
    dispatch("set_items", items_snapshot);
  } catch (error) {
    console.error(error);
    throw new Error("An error occurred while fetching the context's items collection from the database.");
  }
}

export async function maybe_fetch_single_item({ dispatch, getters }, payload) {
  const [cat_id, item_id] = splitPath(payload.path);
  if (!cat_id || !item_id)
    throw Error("Path should contain both category and item id.");
  
  if (!payload.options?.forceFetch && getters.stored_item(cat_id, item_id))
    return getters.single_item(cat_id, item_id);
  else
    return await dispatch("fetch_single_item", { cat_id, item_id });
}

export async function fetch_single_item({ commit, getters }, { cat_id, item_id })  {
  try {
    const path = getters.path(cat_id, item_id);
    const snapshot = await getters.ref
      .collection("items")
      .where("path", "==", path)
      .limit(1)
      .get();

    if (snapshot.docs.length === 0) return null;
    else commit("set_item", snapshot.docs[0]);
    
    return getters.single_item(cat_id, item_id);
  } catch (error) {
    console.error(error);
    throw Error("An error occurred while fetching a single item in the context.");
  }
}

export async function watch_items({ dispatch, commit, getters }) {
  getters.items_listener?.complete();
  const subject = await dispatch("attatch_listener", {
    ref: getters.ref.collection("items"),
    mutation: snapshot => dispatch("set_items", snapshot)
  }, { root: true });
  commit("set_items_listener", subject);
  return await subject.pipe(first()).toPromise();
}

export async function get_from_modules({ dispatch, getters }, payload) {
  switch(pathHead(payload.path)) {
    case "items":
    case "item":
      return dispatch("maybe_fetch_single_item", { ...payload, path: pathTail(payload.path) });
    
    case "notes":
    case "note":
      return dispatch("notes/get", { ...payload, path: pathTail(payload.path) });
    
    default:
      return getters.access(payload.path);
  }
}

export function set_items({ commit }, snapshot) {
  snapshot.docs.forEach(doc => commit("set_item", doc));
}

function forEachItem(context, fn) {
  for (const cat of context.categories) {
    for (const item of cat.items) {
      fn(cat, item);
    }
  }
}